// DE-KPIS-LIVE (docs/design/2026-10-31-de-kpis-live.md): a DE's operating KPIs with its activity
// and cost, from its Record (`digitalEmployees.record`): runs (done, failed or blocked, stopped
// early) and the success rate, cost and cost per run (per outcome is the record's own tile and
// line), spend against its budget,
// the memory its runs read and saved, and its helpers (AG) with what their calls cost. The seat
// drawer's Record tab (`compact`), the DE page's Record tab and KPIs › DE KPIs share it. Short
// labels; every explanation sits behind an (i). A figure with no data reads "—", never 0%.
import { InfoTip, WorkerBadge } from "@companyos/ui";
import type { ReactNode } from "react";
import type { DeRecord, RecordHelper } from "../../api/types";
import { formatDateTime, formatMoney } from "../format";
import { formatRate } from "./record-tab";

export const money = (minor: number | null, currency: string) =>
  minor === null ? "—" : formatMoney(minor, currency);

/** Spend vs budget: "42% of $50.00" or "No budget". */
export function budgetLine(record: Pick<DeRecord, "budget" | "budget_readable" | "currency">): {
  value: string;
  sub: string | null;
  status: string | null;
} {
  const b = record.budget;
  if (!record.budget_readable) return { value: "—", sub: "Shown to managers", status: null };
  if (!b) return { value: "—", sub: "No budget", status: null };
  return {
    value: `${Math.round(b.used * 100)}%`,
    sub: `of ${formatMoney(b.limit_minor, record.currency)}`,
    status: b.status,
  };
}

const STATUS_LABEL: Record<string, string> = {
  on_track: "On pace",
  ahead_of_pace: "Ahead of pace",
  reserve_reached: "Reserve reached",
  no_budget: "No budget",
};

function OpsTile({
  label,
  value,
  sub,
  info,
  field,
  onOpen,
  openLabel = "Open",
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  info: ReactNode;
  field: string;
  onOpen?: (() => void) | undefined;
  openLabel?: string;
}) {
  const body = (
    <>
      <span className="inline-flex min-w-0 items-center gap-1 text-[11px] text-faint">
        <span className="truncate">{label}</span>
      </span>
      <span className="text-sm font-semibold tabular-nums" data-field="value">
        {value}
      </span>
      {sub ? (
        <span className="truncate text-[11px] text-muted-foreground tabular-nums">{sub}</span>
      ) : null}
    </>
  );
  return (
    <div
      className="relative flex min-w-0 flex-col gap-0.5 rounded-md border border-border px-2 py-1.5"
      data-ops-tile={field}
    >
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="flex min-w-0 flex-col gap-0.5 text-left focus-visible:outline-2 focus-visible:outline-ring"
          aria-label={`${label}: ${value}. ${openLabel}`}
        >
          {body}
        </button>
      ) : (
        body
      )}
      <span className="absolute top-1 right-1">
        <InfoTip label={`About ${label}`}>{info}</InfoTip>
      </span>
    </div>
  );
}

/** The operating tiles of one span (the record's total). */
export function DeOpsTiles({
  record,
  compact = false,
  onOpenRuns,
}: {
  record: DeRecord;
  compact?: boolean;
  /** Opens one week's runs (the record's evidence, signal `run`): the latest week that had any. */
  onOpenRuns?: ((week: string) => void) | undefined;
}) {
  const t = record.total;
  const runWeek = record.weeks.findLast((w) => w.runs > 0)?.week ?? null;
  const c = record.currency;
  const budget = budgetLine(record);
  const memoryOn = record.memory.source !== null;
  return (
    <div
      className={
        compact
          ? "grid grid-cols-3 gap-1.5"
          : "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
      }
      data-slot="de-ops"
    >
      <OpsTile
        label="Runs"
        field="runs"
        value={String(t.runs)}
        sub={`${t.runs_done} done · ${t.runs_failed} failed · ${t.runs_stopped} stopped`}
        info="Runs that ended in these weeks. Done: completed and not failed by its verification. Failed: an error, a deadline, a refused step or the budget. Stopped: cancelled, taken over, paused or left unreviewed. Opening it lists the latest week that had runs."
        onOpen={runWeek && onOpenRuns ? () => onOpenRuns(runWeek) : undefined}
        openLabel="Open the latest week's runs"
      />
      <OpsTile
        label="Success"
        field="success"
        value={formatRate(t.run_success_rate)}
        info="Runs done, out of all runs that ended."
      />
      <OpsTile
        label="Cost"
        field="cost"
        value={money(t.cost_minor, c)}
        sub={`${money(t.cost_per_run_minor, c)} / run`}
        info="Model calls, tools and other charges of this DE in these weeks, from the cost ledger (its helpers' calls included)."
      />
      <OpsTile
        label="Budget"
        field="budget"
        value={budget.value}
        sub={budget.status ? `${budget.sub} · ${STATUS_LABEL[budget.status] ?? ""}` : budget.sub}
        info="Spend so far against the DE's budget for the budget's own period, and whether it lasts at this pace."
      />
      <OpsTile
        label="Memory"
        field="memory"
        value={memoryOn ? `${t.memory_reads} · ${t.memory_saves}` : "Off"}
        sub={memoryOn ? "reads · saves" : null}
        info={
          memoryOn
            ? "Reads: brief, resume note and procedure handed to its runs. Saves: what its runs asked memory to keep. Brief use (how much of it a run cited) comes once Recall reports it."
            : "No run of this DE was handed memory yet."
        }
      />
    </div>
  );
}

/** A helper's jobs (its helper cards): "5 jobs · 3 done · 1 blocked · 75%", or "No jobs". */
export function jobsLine(h: RecordHelper): string {
  if (h.jobs === 0) return "No jobs";
  const rate = h.job_success_rate === null ? "" : ` · ${formatRate(h.job_success_rate)}`;
  return `${h.jobs} ${h.jobs === 1 ? "job" : "jobs"} · ${h.jobs_done} done · ${h.jobs_blocked} blocked${rate}`;
}

/** The DE's helpers (AG), each with its jobs and what its calls cost in the window. */
export function DeHelpers({
  helpers,
  currency,
  compact = false,
}: {
  helpers: readonly RecordHelper[];
  currency: string;
  compact?: boolean;
}) {
  if (helpers.length === 0)
    return (
      <p className="text-xs text-faint" data-slot="de-helpers-empty">
        No helpers.
      </p>
    );
  return (
    <ul className="flex flex-col divide-y divide-border" data-slot="de-helpers">
      {helpers.map((h) => (
        <li
          key={h.id}
          className="flex min-w-0 items-center gap-2 py-1.5 text-sm"
          data-helper={h.slug}
        >
          <WorkerBadge kind="ag" />
          <span className="min-w-0 flex-1 truncate">
            {h.name}
            {h.relation === "uses" ? <span className="text-faint"> · shared</span> : null}
            {h.status !== "active" ? (
              <span className="text-faint"> · {h.status.replace("_", " ")}</span>
            ) : null}
          </span>
          {!compact && h.last_activity_at ? (
            <time dateTime={h.last_activity_at} className="text-xs text-muted-foreground">
              {formatDateTime(h.last_activity_at)}
            </time>
          ) : null}
          <span className="text-xs text-muted-foreground tabular-nums" data-field="helper-jobs">
            {jobsLine(h)}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums" data-field="helper-cost">
            {h.calls > 0 ? `${money(h.cost_minor, currency)} · ${h.calls} calls` : "No calls"}
          </span>
        </li>
      ))}
    </ul>
  );
}

type Outcome = DeRecord["outcomes"][number];

/**
 * An outcome KPI's figure by its unit. A percent is a share (0.8) from CompanyOS's own rates, or
 * a percent number (80) as a DE reports one: either reads "80%".
 */
export function outcomeFigure(v: number | null, unit: Outcome["unit"], currency: string): string {
  if (v === null) return "—";
  if (unit === "percent") return `${Math.round(Math.abs(v) <= 1 ? v * 100 : v)}%`;
  if (unit === "currency") return money(Math.round(v), currency);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

const DIRECTION: Partial<Record<Outcome["direction"], string>> = {
  gte: "≥",
  gt: ">",
  lte: "≤",
  lt: "<",
  eq: "=",
};

/** "target ≥ 80% / wk", or null with none. */
export function outcomeTarget(o: Outcome, currency: string): string | null {
  if (o.target === null) return null;
  return `target ${DIRECTION[o.direction] ?? ""} ${outcomeFigure(o.target, o.unit, currency)} / wk`;
}

/**
 * DE-KPIS-LIVE: the outcome KPIs of the DE's role on its seat, over the window: what CompanyOS
 * fed (a count is a real 0 when nothing happened) and what the DE reports ("Not tracked yet"
 * until it has), each with its weekly target.
 */
export function DeOutcomes({ record, compact = false }: { record: DeRecord; compact?: boolean }) {
  if (record.outcomes.length === 0)
    return (
      <p className="text-xs text-faint" data-slot="de-outcomes-empty">
        No outcome KPIs on its seat.
      </p>
    );
  return (
    <div
      className={
        compact
          ? "grid grid-cols-2 gap-1.5"
          : "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4"
      }
      data-slot="de-outcomes"
      data-role={record.outcome_role}
    >
      {record.outcomes.map((o) => (
        <div
          key={o.key}
          className="flex min-w-0 flex-col gap-0.5 rounded-md border border-border px-2 py-1.5"
          data-outcome={o.key}
          data-tracked={o.tracked ? "true" : "false"}
        >
          <span className="truncate text-[11px] text-faint" title={o.label}>
            {o.label}
          </span>
          <span className="text-sm font-semibold tabular-nums" data-field="value">
            {o.tracked ? (
              outcomeFigure(o.value, o.unit, record.currency)
            ) : (
              <span className="font-normal text-muted-foreground">Not tracked yet</span>
            )}
          </span>
          <span className="truncate text-[11px] text-muted-foreground tabular-nums">
            {outcomeTarget(o, record.currency) ?? "No target"}
            {o.fed === "reported" ? " · reported" : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

/** A caption with its (i). */
export function OpsCaption({ label, info }: { label: string; info: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
      {label}
      <InfoTip label={`About ${label}`}>{info}</InfoTip>
    </span>
  );
}
